/**
 * Create/update request body for purchase orders.
 * Contract: see repo root `purchase-orders-api.md` (create + PUT).
 */

import { format } from "date-fns";
import { z } from "zod";

import { dropdownForPayload } from "../shared/locationDropdowns";
import type {
  DropdownOption,
  PurchaseOrderFormLineItem,
  PurchaseOrderFormState,
} from "./purchaseOrderTypes";

const dropdownObjectSchema = z.union([
  z.null(),
  z.object({
    value: z.union([z.string(), z.number()]),
    label: z.string().optional(),
  }),
]);

const purchaseOrderLineItemPayloadSchema = z.object({
  id: z.number().optional(),
  assembly_item_id: z.number(),
  supplier_plu: z.string().nullable(),
  description: z.string().nullable(),
  ordered_amount: z.number(),
  unit_of_measure: dropdownObjectSchema,
  unit_cost: z.number().nullable(),
  lot_number: z.string().nullable(),
  notes: z.string().nullable(),
});

/** Runtime schema for `POST /createPurchaseOrder` and `PUT /purchaseOrders/{id}`. */
export const purchaseOrderUpsertPayloadSchema = z.object({
  supplier: z.object({
    value: z.union([z.string(), z.number()]),
    label: z.string(),
  }),
  order_date: z.string(),
  expected_delivery_date: z.string(),
  location: dropdownObjectSchema,
  notes: z.string().nullable(),
  items: z.array(purchaseOrderLineItemPayloadSchema).min(1),
});

export type PurchaseOrderLineItemPayload = z.infer<
  typeof purchaseOrderLineItemPayloadSchema
>;

export type PurchaseOrderUpsertPayload = z.infer<
  typeof purchaseOrderUpsertPayloadSchema
>;

export interface SupplierDropdownOption {
  value: number | string;
  label?: string;
}

function unitOfMeasureForPayload(
  stored: DropdownOption | null | "" | undefined,
): DropdownOption | null {
  if (stored == null || stored === "") return null;
  if (typeof stored === "object" && stored.value != null) {
    return { value: stored.value, label: stored.label ?? "" };
  }
  return null;
}

/**
 * Build the API request body from form state.
 * Matches `buildPurchaseOrderPayload` in `useCreatePurchaseOrderViewModel`.
 */
export function buildPurchaseOrderPayload(
  purchaseOrder: PurchaseOrderFormState,
  lineItems: PurchaseOrderFormLineItem[],
  supplierOptions: SupplierDropdownOption[],
): PurchaseOrderUpsertPayload {
  const supplierId = purchaseOrder.supplier_id;
  const supplierOption = supplierOptions.find((o) => o.value === supplierId);
  const supplierLabel =
    supplierOption?.label ?? purchaseOrder.supplier_label ?? "";

  const payload: PurchaseOrderUpsertPayload = {
    supplier: { value: supplierId as number | string, label: supplierLabel },
    order_date: purchaseOrder.order_date || format(new Date(), "yyyy-MM-dd"),
    expected_delivery_date: purchaseOrder.expected_delivery_date,
    location: dropdownForPayload(purchaseOrder.location),
    notes: purchaseOrder.notes || null,
    items: lineItems.map((item) => ({
      ...(item.id ? { id: item.id } : {}),
      assembly_item_id: item.assembly_item_id as number,
      supplier_plu: item.supplier_plu || null,
      description: item.description || null,
      ordered_amount: Number(item.ordered_quantity || 0),
      unit_of_measure: unitOfMeasureForPayload(item.unit_of_measure),
      unit_cost: item.unit_cost ? Number(item.unit_cost) : null,
      lot_number: item.expected_lot_number || null,
      notes: item.notes || null,
    })),
  };

  return payload;
}
